import {
  DRAWING_TYPE_LABELS,
  formatPrice,
  formatSigned,
  MIN_DRAWING_OPACITY,
  STOP_LOSS_COLOR,
  supportsOpacity,
  type Drawing,
  type UpdateDrawingInput,
} from '@stockstop/shared';
import { Popover } from '../../components/Popover.js';

/**
 * List of the drawings on the current chart.
 *
 * Covers what dragging cannot: renaming, recolouring, toggling the distance leg
 * and deleting. Selecting a row highlights the drawing on the chart, which is how
 * a drawing scrolled out of view can still be found and removed.
 */

interface DrawingListProps {
  drawings: Drawing[];
  selectedId: number | null;
  currency: string | null;
  onSelect: (id: number | null) => void;
  onUpdate: (id: number, input: UpdateDrawingInput) => void;
  onDelete: (id: number) => void;
  /** Mit der Stop-Loss-Linie verbinden beziehungsweise die Verbindung lösen. */
  onLink: (id: number) => void;
  onUnlink: (id: number) => void;
}

/** A readable summary of a drawing's position. */
const describe = (drawing: Drawing, currency: string | null): string => {
  const { payload } = drawing;
  switch (payload.type) {
    case 'stop_loss':
    case 'horizontal_line':
      return formatPrice(payload.price, { currency });
    case 'trend_line':
      return `${payload.from.date} → ${payload.to.date}`;
    case 'channel':
      return `${payload.from.date} → ${payload.to.date} · Breite ${formatPrice(
        Math.abs(payload.offset),
        { currency },
      )}`;
  }
};

/**
 * Die Palette mit Namen.
 *
 * Ein Hex-Wert ist keine Auskunft: `#7c3aed` sagt niemandem, dass es Violett ist,
 * und in einer Liste aus sechs solchen Werten sucht man die gewünschte Farbe durch
 * Ausprobieren. Der Name steht deshalb vorn, der Hex-Wert als Beleg dahinter.
 */
const PALETTE: readonly { value: string; name: string }[] = [
  /* Ohne Blau: Das gehört der Stop-Loss-Linie. Wäre eine Trendlinie ebenfalls
   * blau, ginge genau die Wiedererkennung verloren, die der festen Farbe ihren
   * Sinn gibt. */
  { value: '#16a34a', name: 'Grün' },
  { value: '#dc2626', name: 'Rot' },
  { value: '#f59e0b', name: 'Orange' },
  { value: '#7c3aed', name: 'Violett' },
  { value: '#0891b2', name: 'Türkis' },
  { value: '#db2777', name: 'Magenta' },
  { value: '#65a30d', name: 'Limette' },
  /* Grau als Rückzug: Eine Hilfslinie, die man behalten, aber nicht mehr
   * beachten will, braucht eine Farbe, die sich nicht vordrängt. */
  { value: '#64748b', name: 'Grau' },
  { value: '#0d9488', name: 'Petrol' },
];

/** Der Name zu einem Hex-Wert; unbekannte Werte nennen sich selbst. */
const colorName = (value: string): string =>
  value === STOP_LOSS_COLOR ? 'Blau' : (PALETTE.find((c) => c.value === value)?.name ?? value);

export const DrawingList = ({
  drawings,
  selectedId,
  currency,
  onSelect,
  onUpdate,
  onDelete,
  onLink,
  onUnlink,
}: DrawingListProps): React.JSX.Element | null => {
  if (drawings.length === 0) {
    return (
      <p className="muted small drawing-list__empty">Noch keine Indikatoren auf diesem Chart.</p>
    );
  }

  /*
   * Es gibt je Chart höchstens **eine** Verbindung. Ist sie vergeben, wird der
   * Knopf bei den übrigen nicht ausgeblendet, sondern gesperrt und erklärt: Ein
   * verschwundener Knopf sähe aus wie ein Indikator, der sich grundsätzlich nicht
   * verbinden lässt.
   */
  const verbundener = drawings.find((drawing) => drawing.linkedDrawingId != null) ?? null;

  return (
    <ul className="drawing-list">
      {drawings.map((drawing) => {
        const isSelected = drawing.id === selectedId;
        const istStopLoss = drawing.payload.type === 'stop_loss';
        const istVerbunden = drawing.linkedDrawingId != null;
        /* Verbunden trägt der Indikator das Blau geliehen — eine Palette wäre ein
         * Versprechen, das der Server zurückweist. */
        const farbeFest = istStopLoss || istVerbunden;
        return (
          <li
            key={drawing.id}
            className={`drawing-list__item${isSelected ? ' is-selected' : ''}`}
            onClick={() => onSelect(isSelected ? null : drawing.id)}
          >
            {/*
              Der Farbtupfer ist zugleich die Bedienung: Er zeigt die Farbe und
              öffnet auf Klick die Palette. Zuvor stand die Farbe zweimal da —
              hier als Tupfer und weiter rechts noch einmal als Auswahlliste.
            */}
            <span
              className="drawing-list__swatch-wrap"
              onClick={(event) => event.stopPropagation()}
            >
              {/*
                Beim Stop-Loss nur ein Tupfer, kein Knopf. Seine Farbe steht fest;
                eine Auswahl, die nichts ändern kann, verspricht eine Freiheit, die
                es nicht gibt.
              */}
              {farbeFest ? (
                <span
                  className="drawing-list__swatch drawing-list__swatch--fest"
                  title={
                    istStopLoss
                      ? 'Die Stop-Loss-Linie ist immer blau'
                      : 'Verbunden — trägt das Blau der Stop-Loss-Linie'
                  }
                >
                  <span
                    className="drawing-list__swatch-dot"
                    style={{ backgroundColor: STOP_LOSS_COLOR }}
                  />
                </span>
              ) : (
                <Popover
                  className="drawing-list__swatch"
                  panelClassName="popover--palette"
                  ariaLabel={`Farbe ändern — derzeit ${colorName(drawing.style.color)}`}
                  title={`Farbe: ${colorName(drawing.style.color)}`}
                  label={
                    <span
                      className="drawing-list__swatch-dot"
                      style={{ backgroundColor: drawing.style.color }}
                    />
                  }
                >
                  {(close) => (
                    <div className="palette" role="group" aria-label="Farbe wählen">
                      {PALETTE.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={`palette__swatch${
                            color.value === drawing.style.color ? ' is-active' : ''
                          }`}
                          style={{ backgroundColor: color.value }}
                          aria-label={color.name}
                          aria-pressed={color.value === drawing.style.color}
                          title={color.name}
                          onClick={() => {
                            onUpdate(drawing.id, { style: { color: color.value } });
                            close();
                          }}
                        />
                      ))}
                    </div>
                  )}
                </Popover>
              )}
            </span>

            <div className="drawing-list__main">
              <input
                className="drawing-list__label"
                value={drawing.label ?? ''}
                placeholder={DRAWING_TYPE_LABELS[drawing.payload.type]}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => onUpdate(drawing.id, { label: event.target.value })}
                aria-label="Beschriftung"
              />
              <span className="muted small">
                {describe(drawing, currency)}
                {istVerbunden ? (
                  <>
                    {' · '}
                    {/*
                      Die festgehaltene Bezugsgröße. Sie steht hier, weil sie sonst
                      nirgends zu sehen ist: Sie ist die 100 %, gegen die täglich
                      verglichen wird, und eine Zahl, die still im Hintergrund über
                      Warnungen entscheidet, sollte nachprüfbar sein.
                    */}
                    <span title="Abstand zum eingebuchten Stop im Moment der Verbindung — die 100 %">
                      {drawing.referenceDistance === null
                        ? 'Bezug offen'
                        : `Bezug ${formatSigned(drawing.referenceDistance, { currency })}`}
                    </span>
                  </>
                ) : null}
              </span>
            </div>

            <div className="drawing-list__actions" onClick={(event) => event.stopPropagation()}>
              {/*
                Die Stop-Loss-Linie bleibt ausgenommen: Sie zeigt den Kurs, zu dem
                verkauft würde, und darf nicht hinter anderes zurücktreten.
              */}
              {supportsOpacity(drawing.payload.type) ? (
                <label className="drawing-list__opacity" title="Deckkraft">
                  <input
                    type="range"
                    min={MIN_DRAWING_OPACITY}
                    max={1}
                    step={0.05}
                    value={drawing.style.opacity}
                    aria-label={`Deckkraft von ${drawing.label ?? DRAWING_TYPE_LABELS[drawing.payload.type]}`}
                    onChange={(event) =>
                      onUpdate(drawing.id, {
                        style: { opacity: Number.parseFloat(event.target.value) },
                      })
                    }
                  />
                  <span className="muted small drawing-list__opacity-value">
                    {Math.round(drawing.style.opacity * 100)} %
                  </span>
                </label>
              ) : null}

              {drawing.payload.type === 'stop_loss' ? (
                <label className="checkbox small" title="Abstand zum letzten Kurs anzeigen">
                  <input
                    type="checkbox"
                    checked={drawing.payload.distanceLeg}
                    onChange={(event) =>
                      onUpdate(drawing.id, {
                        payload: { ...drawing.payload, distanceLeg: event.target.checked },
                      } as UpdateDrawingInput)
                    }
                  />
                  Abstand
                </label>
              ) : null}

              {/*
                Verbinden gibt es nur für die anderen Indikatoren: Die
                Stop-Loss-Linie ist die eine Seite jeder Verbindung und kann nicht
                mit sich selbst verbunden werden.
              */}
              {istStopLoss ? null : istVerbunden ? (
                <button
                  type="button"
                  className="drawing-list__link is-verbunden"
                  onClick={() => onUnlink(drawing.id)}
                  title="Verbindung zur Stop-Loss-Linie lösen — der Indikator bekommt seine Farbe zurück"
                >
                  Verbunden · lösen
                </button>
              ) : (
                <button
                  type="button"
                  className="drawing-list__link"
                  onClick={() => onLink(drawing.id)}
                  disabled={verbundener !== null}
                  title={
                    verbundener === null
                      ? 'Mit der Stop-Loss-Linie verbinden'
                      : `Je Chart ist nur eine Verbindung möglich — derzeit: ${
                          verbundener.label ?? DRAWING_TYPE_LABELS[verbundener.payload.type]
                        }`
                  }
                >
                  Verbinden
                </button>
              )}

              {/*
                Löschen entfällt für die Stop-Loss-Linie und für den verbundenen
                Indikator. Beides weist der Server ohnehin ab; ein Knopf, der nur
                eine Fehlermeldung erzeugt, ist eine Einladung ins Leere.
              */}
              {istStopLoss || istVerbunden ? null : (
                <button
                  type="button"
                  className="button-danger"
                  onClick={() => onDelete(drawing.id)}
                  aria-label={`${drawing.label ?? 'Indikator'} löschen`}
                >
                  Löschen
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
